<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useUserDisplayModeSwitch } from './composables/useUserDisplayModeSwitch'

import type { UserDisplayMode as UserDisplayModeType } from '@/constants/enums/UserDisplayMode'

import { navigateTo } from '#imports'
import { UserDisplayMode } from '@/constants/enums/UserDisplayMode'
import {
  isBuyerAndSupplier,
  saveUserDisplayMode,
} from '@/utils/UserDisplayMode'
import { useAuthStore } from '@/stores/authStore'

/**
 * Compositions
 */
const { t } = useI18n()

/**
 * Types
 */
type Option = {
  title: string
  value: UserDisplayModeType
}

/**
 * Stores
 */
const authStore = useAuthStore()

/**
 * Computed
 */
const userDisplayModeOptions = computed<Option[]>(() => [
  {
    value: UserDisplayMode.BUYER,
    title: t('common.user_display_mode.supply_chain_assessment'),
  },
  {
    value: UserDisplayMode.SUPPLIER,
    title: t('common.user_display_mode.my_assessment'),
  },
])
const activatorLabel = computed<string>(() => {
  const option = userDisplayModeOptions.value.find(
    item => item.value === authStore.userDisplayMode,
  )
  return option ? option.title : ''
})

/**
 * Compositions
 */
const { handleChangeUserDisplayMode } = useUserDisplayModeSwitch(
  authStore.setUserDisplayMode,
  saveUserDisplayMode,
  navigateTo,
)
</script>

<template>
  <!-- 通知パネルよりも前面に出るようにz-indexを設定 -->
  <v-menu
    v-if="
      authStore.company && isBuyerAndSupplier(authStore.company?.contractPlans)
    "
    transition="none"
    :z-index="3001"
  >
    <template #activator="{ props }">
      <div
        v-bind="props"
        class="activator"
      >
        <span>{{ activatorLabel }}</span>
        <v-icon icon="mdi-chevron-down" />
      </div>
    </template>

    <v-card rounded="lg">
      <v-list>
        <v-list-item
          v-for="item in userDisplayModeOptions"
          :key="item.title"
          :title="item.title"
          :value="item.value"
          class="list-item"
          :class="{ 'is-current': item.value === authStore.userDisplayMode }"
          @click="() => handleChangeUserDisplayMode(item.value)"
        />
      </v-list>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
.activator {
  padding-right: 3px;
  padding-left: 8px;
  cursor: pointer;

  span {
    font-size: 12px;
    line-height: 100%;
  }
}

.v-card {
  margin: 4px auto;
}

.v-list-item {
  color: rgb(var(--v-theme-ui-text-black));
  cursor: pointer;

  &.v-list-item--density-default {
    min-height: 0;
  }

  &.list-item {
    padding: 4px 8px;
    text-align: center;
  }

  &.is-current {
    color: rgb(var(--v-theme-ui-graph-blue-2));
  }
}
</style>
